import { HandlerScrollThread } from "./handler-scroll-thread";
import { HandlerRealtime } from "./handler-realtime";
import { useChatStore } from "@/store/chat";
import { ArrowUp, Mic, Square } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/shared/lib/api-client";
import { useVoiceAnswer } from "./use-voice-answer";
import "./handler-conversation.css";
import { HandlerStartingClues } from "./handler-starting-clues";
import { HandlerGraduationIntake } from "./handler-graduation-intake";
import {
  HANDLER_MISSION_API,
  type HandlerMissionFlow,
  type GraduationIntake,
} from "./handler-mission";
import { API_ENDPOINTS } from "@/shared/constants/api-endpoints";
import { useStepperStore } from "@/store/stepper";

import veronicaPortrait from "./veronica-look.png";
import dariusPortrait from "./darius-look.png";

const API = API_ENDPOINTS.companion;
type Message = {
  rootId: string;
  text: string;
  channel: string;
  deleted?: boolean;
};
type Snapshot = { messages: Message[]; workStatus: string };

type OnboardingRecord = { reqKey: string; prompt: string; text: string };

// Read the authenticated member's latest report, not an unscoped query cache
// or an invitation URL's identity. Its stable ID identifies this mission round.
async function resolveMissionCycle(signal: AbortSignal): Promise<string> {
  const { data } = await apiClient.get<{
    result: { missions: { glowId: number }[] };
  }>(API_ENDPOINTS.mission.file, { signal });
  if (!Array.isArray(data?.result?.missions))
    throw new Error("Mission history unavailable");
  return String(data.result.missions[0]?.glowId ?? "first");
}

function buildClueKey(
  adventureId: string | number,
  chatId: string,
  cycle: string,
) {
  // The server-owned private chat ID scopes this to the member and handler.
  return `sm:clues:v1:${adventureId}:${chatId}:${cycle}`;
}

function loadPendingRecords(key: string): OnboardingRecord[] {
  try {
    const raw = localStorage.getItem(`${key}:pending`);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

/** Remove only the acknowledged reqKey from the CURRENT persisted queue. */
function acknowledgePendingRecord(key: string, reqKey: string) {
  try {
    const current = loadPendingRecords(key);
    const filtered = current.filter((r) => r.reqKey !== reqKey);
    if (filtered.length)
      localStorage.setItem(`${key}:pending`, JSON.stringify(filtered));
    else localStorage.removeItem(`${key}:pending`);
  } catch {
    /* storage unavailable */
  }
}

/** Merge a new record into the CURRENT persisted queue (no-op if reqKey exists). */
function appendPendingRecord(key: string, record: OnboardingRecord) {
  try {
    const current = loadPendingRecords(key);
    if (!current.some((r) => r.reqKey === record.reqKey)) current.push(record);
    localStorage.setItem(`${key}:pending`, JSON.stringify(current));
  } catch {
    /* storage unavailable */
  }
}

export function HandlerConversation({
  name,
  onContinue,
  returning = false,
  portrait,
  missionFlow = false,
  onMissionAccepted,
}: {
  name: string;
  onContinue: () => void;
  returning?: boolean;
  portrait?: string;
  missionFlow?: boolean;
  onMissionAccepted?: (flow: HandlerMissionFlow) => void;
}) {
  useEffect(() => {
    useChatStore.getState().enterEmbeddedChat();
    return () => useChatStore.getState().leaveEmbeddedChat();
  }, []);
  const handlerPortrait =
    portrait ||
    (name === "Darius"
      ? dariusPortrait
      : name === "Veronica"
        ? veronicaPortrait
        : undefined);
  const queryClient = useQueryClient();
  const adventureId = useStepperStore((s) => s.adventureId);
  const [mission, setMission] = useState<HandlerMissionFlow | null>(null);
  const [accepting, setAccepting] = useState(false);
  const [editingMission, setEditingMission] = useState(false);
  const [started, setStarted] = useState(returning);
  const [showClues, setShowClues] = useState(missionFlow && !returning);
  const [showGradIntake, setShowGradIntake] = useState(false);
  const [savedGrad, setSavedGrad] = useState<Partial<GraduationIntake> | null>(
    null,
  );
  const gradSubmitting = useRef(false);
  const [gradSaving, setGradSaving] = useState(false);
  const [gradCycle, setGradCycle] = useState("");
  const [returnGreeting, setReturnGreeting] = useState("");
  const [introStage, setIntroStage] = useState(missionFlow ? 0 : 2);

  // --- Clue storage key: null until identity + cycle are resolved ---
  const [clueKey, setClueKey] = useState<string | null>(null);

  const introRecorded = useRef(false);

  // Gate intro animation on resolved identity so a restore skips it
  useEffect(() => {
    if (!missionFlow || (returning && !returnGreeting) || started || !showClues)
      return;
    // First-visit: wait for identity before animating
    if ((!returning && clueKey === null) || introRecorded.current) return;
    const greeting = setTimeout(() => setIntroStage(1), 1200);
    const question = setTimeout(() => setIntroStage(2), 3000);
    return () => {
      clearTimeout(greeting);
      clearTimeout(question);
    };
  }, [missionFlow, returning, returnGreeting, started, showClues, clueKey]);

  const onboarding = useRef<OnboardingRecord[]>([]);
  const recordingHistory = useRef<Promise<void> | null>(null);

  const flushOnboarding = useCallback((): Promise<void> => {
    if (recordingHistory.current) return recordingHistory.current;
    const pending = (async () => {
      while (onboarding.current.length) {
        const item = onboarding.current[0];
        await apiClient.post(`${API.open}onboarding/`, item);
        onboarding.current.shift();
        // Remove only the acked reqKey from current storage, preserving other tabs' records
        if (clueKey) acknowledgePendingRecord(clueKey, item.reqKey);
      }
    })();
    recordingHistory.current = pending.finally(() => {
      recordingHistory.current = null;
    });
    return recordingHistory.current;
  }, [clueKey]);
  const [error, setError] = useState("");
  const record = useCallback(
    (prompt: string, text = "") => {
      // A retry of an unacknowledged answer must retain its request ID.
      if (
        !onboarding.current.some(
          (item) => item.prompt === prompt && item.text === text,
        )
      ) {
        const newRecord: OnboardingRecord = {
          reqKey: crypto.randomUUID(),
          prompt,
          text,
        };
        onboarding.current.push(newRecord);
        // Merge into current storage, preserving other tabs' records
        if (clueKey) appendPendingRecord(clueKey, newRecord);
      }
      if (prompt === "intro" && clueKey) {
        try {
          localStorage.setItem(`${clueKey}:intro`, "1");
        } catch {
          /* unavailable */
        }
      }
      void flushOnboarding().catch(() =>
        setError(
          "Couldn't save this part of the conversation. Please retry before continuing.",
        ),
      );
    },
    [flushOnboarding, clueKey],
  );
  useEffect(() => {
    if (
      missionFlow &&
      !returning &&
      !started &&
      introStage >= 1 &&
      !introRecorded.current
    ) {
      introRecorded.current = true;
      record("intro");
    }
  }, [missionFlow, returning, started, introStage, record]);
  const [attempt, setAttempt] = useState(0);
  const [chat, setChat] = useState("");
  const [messages, setMessages] = useState<Message[]>([]);
  const [draft, setDraft] = useState("");
  const pendingAnswers = useRef<{ text: string; key: string } | null>(null);
  const [work, setWork] = useState("IDLE");
  const [sending, setSending] = useState(false);
  const [inputMode, setInputMode] = useState<"text" | "voice">("voice");
  const relayReady = useRef(false);
  const refreshHistory = useRef<() => void>(() => {});
  const requestKey = useRef<{ text: string; key: string } | null>(null);
  const inFlight = useRef(false);

  const mounted = useRef(true);
  const bottom = useRef<HTMLDivElement>(null);
  const voice = useVoiceAnswer(setDraft);
  const recording = voice.status === "recording";
  const busy = sending || work === "QUEUED" || work === "WORKING";

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  // --- First-visit identity resolution: get chatId + cycle before showing picker ---
  useEffect(() => {
    if (!missionFlow || returning || started || !showClues || clueKey) return;
    const controller = new AbortController();
    void (async () => {
      try {
        // Parent already POSTed companion.open when choosing handler; GET returns it
        const { data: companion } = await apiClient.get<{
          result: { chatId: string | null; handler: string | null };
        }>(API.open, { signal: controller.signal });
        if (controller.signal.aborted) return;
        const resolvedChat = companion.result.chatId;
        if (!resolvedChat || companion.result.handler !== name)
          throw new Error("Handler identity unavailable");
        setChat(resolvedChat);
        const filed = await resolveMissionCycle(controller.signal);
        if (controller.signal.aborted) return;
        const key = buildClueKey(adventureId, resolvedChat, filed);
        // A completed picker can still have another tab's unacknowledged answers.
        // Await them in order before switching screens (which aborts this effect).
        let cluesDone = false;
        try {
          cluesDone = !!localStorage.getItem(`${key}:done`);
        } catch {
          /* storage unavailable */
        }
        if (cluesDone) {
          for (const rec of loadPendingRecords(key)) {
            await apiClient.post(`${API.open}onboarding/`, rec, {
              signal: controller.signal,
            });
            acknowledgePendingRecord(key, rec.reqKey);
          }
          if (controller.signal.aborted) return;
          setShowClues(false);
          setStarted(true);
          setClueKey(key);
          return;
        }
        // If a draft exists, skip intro animation (no duplicate intro on refresh)
        try {
          if (
            localStorage.getItem(key) ||
            localStorage.getItem(`${key}:intro`)
          ) {
            introRecorded.current = true;
            setIntroStage(2);
          }
        } catch {
          /* storage unavailable */
        }
        // Restore pending onboarding records (unsent before refresh)
        const pending = loadPendingRecords(key);
        if (pending.length && !onboarding.current.length) {
          onboarding.current = pending;
          setError(
            "Some answers are waiting to save. Retry to finish saving them.",
          );
          // Keep pending request IDs until the user retries or answers.
        }
        setClueKey(key);
      } catch {
        if (!controller.signal.aborted)
          setError("Couldn't restore your conversation. Retry to reconnect.");
      }
    })();
    return () => controller.abort();
  }, [
    missionFlow,
    returning,
    started,
    showClues,
    clueKey,
    adventureId,
    name,
    attempt,
  ]);

  useEffect(() => {
    if (!started) return;
    const controller = new AbortController();
    let timer: ReturnType<typeof setTimeout>;
    let polling = false;
    let dirty = false;
    const poll = async (id: string) => {
      if (controller.signal.aborted) return;
      if (polling) {
        dirty = true;
        return;
      }
      polling = true;
      clearTimeout(timer);
      try {
        const { data } = await apiClient.get<{ result: Snapshot }>(
          API.history(id),
          { signal: controller.signal },
        );
        if (controller.signal.aborted) return;
        setError("");
        setMessages(data.result.messages);
        setWork(data.result.workStatus);
        if (missionFlow && data.result.workStatus === "IDLE") {
          const result = await apiClient.get<{
            result: { flow: HandlerMissionFlow | null };
          }>(HANDLER_MISSION_API, { signal: controller.signal });
          if (!controller.signal.aborted) {
            const flow = result.data.result.flow;
            setMission(flow);
          }
        }
        if (data.result.workStatus === "FAILED")
          setError(
            missionFlow
              ? "Your handler couldn't reply. Your conversation is saved; please try another reply."
              : "Your handler couldn't reply. You can keep going with your mission.",
          );
      } catch {
        if (!controller.signal.aborted)
          setError("Connection interrupted. Reconnecting…");
      }
      polling = false;
      if (!controller.signal.aborted)
        timer = setTimeout(
          () => void poll(id),
          dirty ? 0 : relayReady.current ? 15000 : 2000,
        );
      dirty = false;
    };
    void apiClient
      .post<{ result: { chatId: string } }>(
        API.open,
        { handler: name },
        { signal: controller.signal },
      )
      .then(async ({ data }) => {
        if (controller.signal.aborted) return;
        refreshHistory.current = () => void poll(data.result.chatId);
        setChat(data.result.chatId);
        setError("");
        if (missionFlow && returning) {
          const begin = await apiClient.post<{
            result: {
              flow?: HandlerMissionFlow | null;
              guidedIntake?: boolean;
              greeting?: string;
              graduationNeeded?: boolean;
              grad?: GraduationIntake | null;
              graduationCycle?: string;
            };
          }>(
            HANDLER_MISSION_API,
            { begin: true },
            { signal: controller.signal },
          );
          if (controller.signal.aborted) return;
          // Mission 7: show graduation intake from explicit server flag
          if (begin.data.result.graduationNeeded) {
            setGradCycle(begin.data.result.graduationCycle || "mission7");
            if (begin.data.result.grad) setSavedGrad(begin.data.result.grad);
            setShowGradIntake(true);
            setStarted(false);
            return;
          }
          if (begin.data.result.guidedIntake) {
            // Resolve cycle scope for returning guided intake
            const filed = await resolveMissionCycle(controller.signal);
            if (controller.signal.aborted) return;
            const key = buildClueKey(adventureId, data.result.chatId, filed);
            // guidedIntake=true means the current intake is unfinished —
            // do NOT clear saved progress, it belongs to THIS cycle
            const hasDraft = (() => {
              try {
                return !!localStorage.getItem(key);
              } catch {
                return false;
              }
            })();
            // Restore pending onboarding records
            const pending = loadPendingRecords(key);
            if (pending.length && !onboarding.current.length) {
              onboarding.current = pending;
              setError(
                "Some answers are waiting to save. Retry to finish saving them.",
              );
            }
            if (hasDraft) {
              // Skip greeting/intro animation — restoring mid-conversation
              introRecorded.current = true;
              setIntroStage(2);
            } else {
              setReturnGreeting(begin.data.result.greeting || "");
              setIntroStage(begin.data.result.greeting ? 0 : 2);
            }
            setClueKey(key);
            setShowClues(true);
            setStarted(false);
            return;
          }
        }
        const pending = pendingAnswers.current;
        if (pending) {
          setSending(true);
          requestKey.current = pending;
          try {
            await apiClient.post(API.messages(data.result.chatId), {
              reqKey: pending.key,
              text: pending.text,
              kind: "TEXT",
            });
            if (controller.signal.aborted) return;
            pendingAnswers.current = null;
            requestKey.current = null;
            setWork("QUEUED");
          } catch {
            if (!controller.signal.aborted) {
              setDraft(pending.text);
              setError(
                "Couldn't send your answers. They're saved below; tap Send answer to retry.",
              );
            }
          } finally {
            if (!controller.signal.aborted) setSending(false);
          }
        }
        if (!controller.signal.aborted) void poll(data.result.chatId);
      })
      .catch(() => {
        if (!controller.signal.aborted)
          setError(
            missionFlow
              ? "Couldn't reach your handler. Retry to reconnect."
              : "Couldn't reach your handler. Retry or continue to your mission.",
          );
      });
    return () => {
      controller.abort();
      refreshHistory.current = () => {};
      relayReady.current = false;
      clearTimeout(timer);
    };
  }, [started, name, attempt, missionFlow, returning, adventureId]);

  const send = async () => {
    const text = draft.trim();
    if (!text || !chat || inFlight.current || busy || voice.busy) return;
    inFlight.current = true;
    setSending(true);
    setError("");
    if (requestKey.current?.text !== text)
      requestKey.current = { text, key: crypto.randomUUID() };
    try {
      await apiClient.post(API.messages(chat), {
        reqKey: requestKey.current.key,
        text,
        kind: "TEXT",
      });
      if (!mounted.current) return;
      setDraft("");
      requestKey.current = null;
      pendingAnswers.current = null;
      setWork("QUEUED");
      setEditingMission(false);
    } catch {
      if (mounted.current)
        setError(
          "Couldn't send that. Your words are still here; tap Send to retry.",
        );
    } finally {
      inFlight.current = false;
      if (mounted.current) setSending(false);
    }
  };
  const acceptMission = async () => {
    if (!mission || busy || accepting || voice.busy) return;
    setAccepting(true);
    try {
      const { data } = await apiClient.post<{
        result: { flow: HandlerMissionFlow };
      }>(HANDLER_MISSION_API, { accept: mission.id });
      if (mounted.current) onMissionAccepted?.(data.result.flow);
    } catch {
      if (mounted.current)
        setError(
          "Couldn't accept that mission. Your proposal is still here; please retry.",
        );
    } finally {
      if (mounted.current) setAccepting(false);
    }
  };
  const leave = () => {
    if (!returning)
      window.dispatchEvent(
        new CustomEvent("sm:handler-introduced", { detail: name }),
      );
    void queryClient.invalidateQueries({ queryKey: ["mission"] });
    onContinue();
  };

  return (
    <section
      data-handler={name === "Veronica" ? "veronica" : "darius"}
      className={`handler-conversation${missionFlow ? " handler-contact-chat" : ""}${missionFlow && mission && !busy && !editingMission && ["review", "accepted"].includes(mission.phase) ? " handler-review-active" : ""}${!started ? " handler-introduction" : ` handler-chat-active${inputMode === "voice" ? " handler-voice-active" : ""}`}`}
      aria-label={`Talk to ${name}`}
    >
      {chat && (
        <HandlerRealtime
          chatId={chat}
          onUpdate={() => refreshHistory.current()}
          onConnected={(ready) => {
            relayReady.current = ready;
          }}
        />
      )}
      <header className={missionFlow ? "handler-contact-header" : undefined}>
        {missionFlow && handlerPortrait && <img src={handlerPortrait} alt="" />}
        <div>
          <span>Your handler</span>
          <h1>{name}</h1>
        </div>
      </header>
      {!started && error && (
        <div role="alert">
          {error}
          <button
            type="button"
            onClick={() => {
              setError("");
              setAttempt((value) => value + 1);
              const finishing = onboarding.current.some(
                (item) => item.prompt === "finish",
              );
              void flushOnboarding()
                .then(() => {
                  if (finishing) {
                    if (clueKey) {
                      try {
                        localStorage.setItem(`${clueKey}:done`, "1");
                        localStorage.removeItem(clueKey);
                      } catch {
                        /* storage unavailable */
                      }
                    }
                    setStarted(true);
                    setWork("QUEUED");
                  }
                })
                .catch(() =>
                  setError("Couldn't save your answers. Please retry."),
                );
            }}
          >
            Retry connection
          </button>
        </div>
      )}
      {!started && showGradIntake ? (
        <HandlerGraduationIntake
          name={name}
          storageKey={`sm:graduation:${chat}:${gradCycle}`}
          submitting={gradSaving}
          initial={savedGrad ?? undefined}
          onComplete={async (grad) => {
            if (gradSubmitting.current) return;
            gradSubmitting.current = true;
            setGradSaving(true);
            try {
              await apiClient.post(HANDLER_MISSION_API, { graduation: grad });
              try {
                localStorage.removeItem(`sm:graduation:${chat}:${gradCycle}`);
              } catch {
                /* storage may be full or unavailable */
              }
              setShowGradIntake(false);
              setStarted(true);
            } catch {
              setError(
                "Couldn't save your graduation choices. Please try again.",
              );
            } finally {
              gradSubmitting.current = false;
              setGradSaving(false);
            }
          }}
        />
      ) : !started && showClues ? (
        missionFlow && clueKey === null ? (
          // Wait for identity/cycle resolution before mounting picker
          <p role="status">Connecting…</p>
        ) : (
          <HandlerScrollThread
            className="handler-clues-thread"
            role="log"
            aria-label={`Conversation with ${name}`}
          >
            {returning && returnGreeting && introStage >= 1 && (
              <div className="handler-message-row">
                <div className="handler-bubble">
                  <small>{name}</small>
                  <p>{returnGreeting}</p>
                </div>
              </div>
            )}
            {!returning && introStage >= 1 && (
              <div className="handler-message-row">
                <div className="handler-bubble">
                  <small>{name} → you</small>
                  <p>
                    Hey, I’m {name}. I’m 100% pure AI.{" "}
                    {name === "Veronica"
                      ? "Very real enthusiasm for a little mischief, though."
                      : "The scheming comes with the territory."}
                  </p>
                </div>
              </div>
            )}
            {introStage < 2 ? (
              <div
                className="handler-message-row"
                role="status"
                aria-label={`${name} is typing`}
              >
                <div className="handler-bubble">
                  <span className="handler-typing-dots" aria-hidden="true">
                    <i />
                    <i />
                    <i />
                  </span>
                </div>
              </div>
            ) : (
              <HandlerStartingClues
                name={name}
                key={clueKey ?? "unpersisted"}
                storageKey={clueKey ?? undefined}
                onRecord={missionFlow ? record : undefined}
                onComplete={async (text) => {
                  try {
                    await flushOnboarding();
                  } catch {
                    setError(
                      "Couldn't save your conversation. Tap your last answer again to retry.",
                    );
                    return;
                  }
                  // Mark clues done so a refresh skips straight to chat
                  if (clueKey) {
                    try {
                      localStorage.setItem(`${clueKey}:done`, "1");
                      localStorage.removeItem(clueKey);
                    } catch {
                      /* storage unavailable */
                    }
                  }
                  if (missionFlow) {
                    setWork("QUEUED");
                  } else setDraft(text);
                  setStarted(true);
                }}
              />
            )}
          </HandlerScrollThread>
        )
      ) : !started ? (
        <div className="handler-invite">
          {handlerPortrait && (
            <img
              className="handler-invite-portrait"
              src={handlerPortrait}
              alt={name}
            />
          )}
          <h2>Let your handler get to know you.</h2>
          <p>
            Share a little about the people, places, and things that matter to
            you. Your handler can use what you share to make future missions
            more personal.
          </p>
          <button onClick={() => setShowClues(true)}>Talk to {name}</button>
          <button className="handler-quiet" onClick={leave}>
            Skip for now
          </button>
        </div>
      ) : (
        <>
          {inputMode === "voice" && !missionFlow && handlerPortrait && (
            <img
              className="handler-chat-portrait"
              src={handlerPortrait}
              alt={name}
            />
          )}
          <div className="handler-chat-panel">
            {inputMode === "voice" && !missionFlow ? (
              <div className="handler-voice-prompt" aria-live="polite">
                <p>
                  {busy
                    ? `${name} is thinking…`
                    : !chat
                      ? error
                        ? ""
                        : "Connecting…"
                      : [...messages]
                          .reverse()
                          .find(
                            (message) =>
                              message.channel === "AI" && !message.deleted,
                          )?.text ||
                        (draft
                          ? "Check your answers below, then send them when you’re ready."
                          : `Hey, I'm ${name}. What's been taking up most of your time lately?`)}
                </p>
              </div>
            ) : (
              <HandlerScrollThread
                className="handler-messages"
                role="log"
                aria-label="Conversation"
                aria-live="polite"
              >
                {!missionFlow && messages.length === 0 && (
                  <div className="handler-message-row">
                    <p className="handler-bubble">
                      Hey, I'm {name}. Before we get to your next mission, I'd
                      like to know a little about your world. What's been taking
                      up most of your time lately?
                    </p>
                  </div>
                )}
                {messages
                  .filter((m) => !m.deleted)
                  .map((m) => (
                    <div
                      key={m.rootId}
                      className="handler-message-row"
                      data-member={m.channel !== "AI"}
                    >
                      <p
                        className="handler-bubble"
                        data-member={m.channel !== "AI"}
                      >
                        {m.text}
                      </p>
                    </div>
                  ))}
                {busy && (
                  <p role="status" className="handler-thinking">
                    <span className="handler-typing-dots" aria-hidden="true">
                      <i />
                      <i />
                      <i />
                    </span>
                    <span>{name} is replying</span>
                  </p>
                )}
                {!chat && !error && <p role="status">Connecting…</p>}
                {missionFlow &&
                  mission &&
                  !busy &&
                  !editingMission &&
                  ["review", "accepted"].includes(mission.phase) && (
                    <section
                      className="handler-mission-review"
                      aria-label="Your proposed mission"
                    >
                      <h2>{mission.mission.name}</h2>
                      <p>{mission.mission.mission}</p>
                      {mission.mission.helpText && (
                        <p>{mission.mission.helpText}</p>
                      )}
                      <button
                        className="handler-continue-mission"
                        disabled={accepting || voice.busy}
                        onClick={() => void acceptMission()}
                      >
                        {accepting
                          ? "Saving…"
                          : mission.phase === "accepted"
                            ? "Continue my accepted mission"
                            : "Accept this mission"}
                      </button>
                      {mission.phase === "review" && (
                        <button
                          className="handler-quiet"
                          onClick={() => {
                            setEditingMission(true);
                            setInputMode("text");
                            setDraft("I'd like to adjust the mission: ");
                          }}
                        >
                          Let’s adjust it
                        </button>
                      )}
                    </section>
                  )}
                <div ref={bottom} />
              </HandlerScrollThread>
            )}
            {error && (
              <div role="alert">
                <p>{error}</p>
                {!chat && (
                  <button onClick={() => setAttempt((a) => a + 1)}>
                    Retry connection
                  </button>
                )}
              </div>
            )}
            {voice.error && (
              <p role="alert">
                {voice.error} You can switch to text with Can’t talk.
              </p>
            )}
            <form
              className={`handler-compose${inputMode === "voice" ? " handler-compose-voice" : ""}`}
              onSubmit={(e) => {
                e.preventDefault();
                void send();
              }}
            >
              <label className="sr-only" htmlFor="handler-answer">
                Your message
              </label>
              {(inputMode === "text" || !!draft) && (
                <textarea
                  id="handler-answer"
                  placeholder="Say what's on your mind…"
                  value={draft}
                  maxLength={6000}
                  disabled={sending || voice.busy}
                  onChange={(e) => setDraft(e.target.value)}
                  rows={inputMode === "voice" ? 2 : 1}
                />
              )}
              <div className="handler-compose-actions">
                {inputMode === "text" && (
                  <button
                    type="button"
                    className="handler-use-voice"
                    aria-label="Use voice"
                    title="Use voice"
                    disabled={voice.busy}
                    onClick={() => setInputMode("voice")}
                  >
                    <Mic size={20} aria-hidden="true" />
                  </button>
                )}
                {inputMode === "voice" && (
                  <button
                    type="button"
                    disabled={!chat || busy || (voice.busy && !recording)}
                    aria-label={recording ? "Stop recording" : "Tap to speak"}
                    onClick={() =>
                      recording ? voice.stop() : void voice.start()
                    }
                  >
                    {recording ? (
                      <Square size={18} aria-hidden="true" />
                    ) : (
                      <Mic size={20} aria-hidden="true" />
                    )}
                    <span className="handler-mic-label">
                      {recording
                        ? "Tap to stop"
                        : voice.status === "transcribing"
                          ? "Transcribing…"
                          : voice.status === "requesting"
                            ? "Connecting…"
                            : "Tap to speak"}
                    </span>
                    {recording && (
                      <span className="handler-wave" aria-hidden="true">
                        {voice.audioLevels.map((level, i) => (
                          <i key={i} style={{ height: 3 + level * 22 }} />
                        ))}
                      </span>
                    )}
                  </button>
                )}
                {!!draft.trim() && (
                  <button
                    type="submit"
                    className="handler-send"
                    aria-label="Send"
                    title="Send message"
                    disabled={!draft.trim() || !chat || busy || voice.busy}
                  >
                    {inputMode === "voice" ? (
                      "Send answer"
                    ) : (
                      <ArrowUp size={21} strokeWidth={2.5} aria-hidden="true" />
                    )}
                  </button>
                )}
              </div>
            </form>
            {inputMode === "voice" && (
              <button
                className="handler-quiet handler-input-toggle"
                disabled={voice.busy}
                onClick={() => setInputMode("text")}
              >
                Can’t talk
              </button>
            )}

            <footer>
              {!missionFlow && (
                <button className="handler-continue-mission" onClick={leave}>
                  {returning ? "Back to my mission" : "Continue to my mission"}
                </button>
              )}
            </footer>
          </div>
        </>
      )}
    </section>
  );
}
